import { createContext, useContext, useEffect, useState } from 'react';
import { User, AuthError } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signUp: (email: string, password: string) => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signUpWithGoogle: (betaCode: string) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updateProfile: (data: { full_name?: string; avatar_url?: string }) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check active sessions and sets the user
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setLoading(false);
    });

    // Listen for changes on auth state
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`,
        },
      });
      if (error) throw error;
      // Don't show toast or throw error on success
      // The success page will handle the messaging
    } catch (error) {
      const authError = error as AuthError;
      toast.error(authError.message);
      throw error;
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (error) throw error;
      toast.success('Successfully signed in!');
    } catch (error) {
      const authError = error as AuthError;
      toast.error(authError.message);
      throw error;
    }
  };

  const signInWithGoogle = async () => {
    try {
      const currentUrl = window.location.origin;
      console.log('Debug OAuth Flow:', {
        currentUrl,
        fullUrl: window.location.href,
        hostname: window.location.hostname,
        port: window.location.port,
        protocol: window.location.protocol
      });

      const redirectUrl = `${window.location.origin}/auth/callback`;

      console.log('Using redirect URL:', redirectUrl);

      // Log the full OAuth options
      const oauthOptions = {
        provider: 'google' as const,
        options: {
          redirectTo: redirectUrl,
        },
      };
      console.log('OAuth Options:', JSON.stringify(oauthOptions, null, 2));

      const { data, error } = await supabase.auth.signInWithOAuth(oauthOptions);

      console.log('Supabase OAuth Response:', JSON.stringify({ 
        data, 
        error,
        url: data?.url,
        provider: data?.provider
      }, null, 2));

      if (error) throw error;
    } catch (error) {
      const authError = error as AuthError;
      console.error('OAuth Error:', JSON.stringify(authError, null, 2));
      toast.error(authError.message);
      throw error;
    }
  };

  const signUpWithGoogle = async (betaCode: string) => {
    try {
      // Validate beta code first
      const { data: isValidCode, error: codeError } = await supabase
        .rpc('validate_beta_code', { code: betaCode });

      if (codeError || !isValidCode) {
        toast.error('Invalid or expired beta code');
        return;
      }

      // Store beta code in localStorage for later use
      localStorage.setItem('pendingBetaCode', betaCode);

      // Proceed with Google OAuth
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
        },
      });

      if (error) throw error;
    } catch (error) {
      const authError = error as AuthError;
      toast.error(authError.message);
      throw error;
    }
  };

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      toast.success('Successfully signed out!');
    } catch (error) {
      const authError = error as AuthError;
      toast.error(authError.message);
      throw error;
    }
  };

  const resetPassword = async (email: string) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth/reset-password`,
      });
      if (error) throw error;
      toast.success('Password reset instructions sent to your email!');
    } catch (error) {
      const authError = error as AuthError;
      toast.error(authError.message);
      throw error;
    }
  };

  const updateProfile = async (data: { full_name?: string; avatar_url?: string }) => {
    try {
      const { error } = await supabase
        .from('profiles')
        .update(data)
        .eq('id', user?.id);
      
      if (error) throw error;
      toast.success('Profile updated successfully!');
    } catch (error) {
      toast.error('Error updating profile');
      throw error;
    }
  };

  return (
    <AuthContext.Provider 
      value={{ 
        user, 
        loading, 
        signUp, 
        signIn, 
        signInWithGoogle,
        signUpWithGoogle,
        signOut, 
        resetPassword,
        updateProfile
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 